
import { IBreakdownReport, breakdownReportState } from '../../../../interfaces/calculator.interface';
import { IInterventionsChecklist, InterventionsChecklistForm } from '../../../../interfaces/interventions.interface';
import { StringFormatter, Constants } from '../../../../utils';

export const getCostSavingsReportBreakdown = (
  interventionsChecklist: IInterventionsChecklist,
  report: IBreakdownReport = breakdownReportState
) => {

  const formatPerc = (value: string) =>
    (value && value !== Constants.STRING_TYPES.EMPTY) ?
      StringFormatter.percentage(value) : Constants.STRING_TYPES.EMPTY;

  return [
    {
      title: 'Interventions for Children',
      resultsHeaders: ['# or %', 'Low Estimate', 'High Estimate'],
      subCategories: [
        {
          title: 'Oral Health',
          visible: interventionsChecklist[InterventionsChecklistForm.OralHealth],
          lines: [
            {
              description: 'Number of caregivers who discussed a need for oral health services for their Medicaid/CHIP-enrolled child(ren) (ages 0-3) with a HS Specialist and/or were referred for services',
              values: [report.oralHealth_needForOralHealth, '', ''],
              displayDefault: true,
              note: report.oralHealth_needForOralHealthNote,
            },
            {
              description: 'Percentage of Medicaid/CHIP-enrolled children who had a first dental visit by age 3 due to HealthySteps Specialist(s) support',
              values: ['', formatPerc(report.oralHealth_lowEstimate), formatPerc(report.oralHealth_highEstimate)],
              displayDefault: true,
              note: report.oralHealth_estimatesNote,
            }
          ]
        },
        {
          title: 'Asthma',
          visible: interventionsChecklist.asthma,
          lines: [
            {
              description: 'Number of Tier 2 and 3 HealthySteps Medicaid/CHIP-enrolled children ages 0-3 who have asthma or are wheezing and at risk for asthma',
              values: [report.asthma_numberHaveAsthma, '', ''],
              displayDefault: true,
              note: report.asthma_numberHaveAsthmaNote,
            },
            {
              description: 'Estimated percentage of Tier 2 and 3 HealthySteps Medicaid/CHIP-enrolled children who are able to avoid ED visits due to HealthySteps',
              values: ['', formatPerc(report.asthma_lowEstimate), formatPerc(report.asthma_highEstimate)],
              displayDefault: true,
              note: report.asthma_estimatesNote,
            }
          ],
        },
        {
          title: 'Appropriate Use of Care',
          visible: interventionsChecklist.emergencyDepartmentUse,
          lines: [
            {
              description: 'Number of caregivers (of Medicaid/CHIP-enrolled children) who contacted HealthySteps Specialist(s) seeking advice about whether to bring a sick child in for a visit',
              values: [report.emergencyDepartment_contactedByPhone, '', ''],
              displayDefault: true,
              note: report.emergencyDepartment_contactedByPhoneNote,
            },
            {
              description: 'Number of well-child visits for Medicaid/CHIP-enrolled children that occurred due to HS Specialist(s) contacting families to triage barriers and/or encourage attendance',
              values: [report.emergencyDepartment_numberOfEncourageVisits, '', ''],
              displayDefault: true,
            },
            {
              description: 'Percentage of caregivers (of Medicaid/CHIP-enrolled children) seeking advice about a sick child who ended up treating the child at home for a minor illness but would have brought the child in for a visit (for example, at a doctor’s office, urgent care, or hospital emergency department) if they had not talked with a HealthySteps Specialist',
              values: [formatPerc(report.emergencyDepartment_treatedAtHome), '', ''],
              displayDefault: true,
              note: report.emergencyDepartment_treatedAtHomeNote,
            },
            {
              description: 'Percentage of caregivers (of Medicaid/CHIP-enrolled children) seeking advice about a sick child who ended up bringing the child in for a clinic or other office-based visit (including urgent care) but would have brought the child in for a hospital emergency department visit if they had not talked with a HealthySteps Specialist',
              values: [formatPerc(report.emergencyDepartment_clinicVisit), '', ''],
              displayDefault: true,
              note: report.emergencyDepartment_clinicVisitNote,
            }
          ],
        },
        {
          title: 'Immunizations',   
                 
          visible: interventionsChecklist.immunizations,
          lines: [
            {
              description: 'Number of caregivers who discussed a need for flu vaccines for their Medicaid/CHIP-enrolled children with a HS Specialist and/or were assisted in pursuing the flu vaccine',
              values: [report.immunizations_needForFluVaccinations, '', ''],
              displayDefault: true,
              note: report.immunizations_needForFluVaccinationsNote,
            },
            {
              description: 'Percent of Medicaid/CHIP-enrolled children who received the vaccination series due to HS',
              values: ['', formatPerc(report.immunizations_lowEstimate), formatPerc(report.immunizations_highEstimate)],
              displayDefault: true,
              note: report.immunizations_estimatesNote,
            }
          ]
        },
        {
          title: 'Early Childhood Vaccination Series',                    
          visible: interventionsChecklist.immunizations,
          lines: [
            {
              description: 'Number of caregivers who discussed early childhood vaccinations for Medicaid/CHIP-enrolled children ages 0-18 months with a HS Specialist and/or were assisted in pursuing the vaccines',
              values: [report.immunizations_numberOfCaregiversDiscussingECVWithHSS, '', ''],
              displayDefault: true,
              note: report.immunizations_numberOfCaregiversDiscussingECVWithHSSNote,
            },
            {
              description: 'Percent of Medicaid/CHIP-enrolled children who were unvaccinated and received the flu vaccine due to HS involvement',
              values: ['', formatPerc(report.immunizations_longTerm_lowEstimate), formatPerc(report.immunizations_longTerm_highEstimate)],
              displayDefault: true,
              note: report.immunizations_estimatesNote,
            }
          ]
        },
         {
          title: 'Early Detection',
          visible: interventionsChecklist.earlyDetection,
          lines: [
            {
              description: 'Number of children (ages 0-3) referred by HS for Head Start/Early Head Start/other educational preschool programs',
              values: [report.earlyDetection_numberOfChildrenReferredToEarlyEducationPrograms, '', ''],
              displayDefault: true,
              note: report.earlyDetection_numberOfChildrenReferredToEarlyEducationProgramsNote,
            },
            {
              description: 'Number of children (ages 0-3) identified through screening and/or surveillance (e.g., ASQ-3, ASQ-SE:2, SWYC) and referred by HS for Early Intervention services',
              values: [report.earlyDetection_numberOfChildrenReferredToEarlyIntervention, '', ''],
              displayDefault: true,
              note: report.earlyDetection_numberOfChildrenReferredToEarlyEducationProgramsNote,
            },
            {
              description: 'Percent of children referred for Head Start/Early Head Start/other educational preschool programs who enroll and attend',
              values: ['', formatPerc(report.earlyDetection_lowEstimate), formatPerc(report.earlyDetection_highEstimate)],
              displayDefault: true,
              note: report.earlyDetection_estimatesNote,
            },
            {
              description: 'Percent of children referred who receive EI services due to HS involvement',
              values: [formatPerc(report.earlyDetection_percentageOfChildrenReceivingEarlyInterventionServices), '', ''],
              displayDefault: true,
              note: report.earlyDetection_estimatesNote,
            },
          ]
        },
      ],
    },
    {
      title: 'Interventions for Caregivers',
      subCategories: [
        {
          title: 'Postpartum Maternal Depression',
          visible: interventionsChecklist.postpartumMaternalDepression,
          lines: [
            {
              description: 'Number of Medicaid-enrolled caregivers who discussed postpartum maternal depression within 12 months of delivery with a HS Specialist (either in-person or telephonically)',
              values: [report.postpartumMaternalDepression_discussedProblem, '', ''],
              displayDefault: true,
              note: report.postpartumMaternalDepression_discussedProblemNote,
            },
            {
              description: 'Number of Medicaid-enrolled mothers who received counseling or related services from a HS Specialist directly and/or were referred for additional services for postpartum maternal depression',
              values: [report.postpartumMaternalDepression_receivedCounseling, '', ''],
              displayDefault: true,
              note: report.postpartumMaternalDepression_receivedCounselingNote,
            },
            // {
            //   description: 'Number of mothers that the HS Specialist(s) referred for counseling or other services for postpartum maternal depression',
            //   values: [report.postpartumMaternalDepression_referredForServices, '', ''],
            //   displayDefault: true,
            //   note: report.postpartumMaternalDepression_referredForServicesNote,
            // },
            {
              description: 'Percentage reduction in number of mothers with persistent depression due to HealthySteps',
              values: ['', formatPerc(report.postpartumMaternalDepression_lowEstimate), formatPerc(report.postpartumMaternalDepression_highEstimate)],
              displayDefault: true,
              note: report.postpartumMaternalDepression_estimatesNote,
            }
          ]
        },
        {
          title: 'Healthy Birth Spacing',
          visible: interventionsChecklist.healthyBirthSpacing,
          lines: [
            {
              description: 'Number of mothers who discussed a need for contraception or other family planning services with a HS Specialist and/or were referred for services (e.g., postpartum visit, family planning, contraception, etc.',
              values: [report.healthyBirthSpacing_needForContraception, '', ''],
              displayDefault: true,
              note: report.healthyBirthSpacing_needForContraceptionNote,
            },
            {
              description: 'Percentage of Medicaid-enrolled mothers who followed up on referrals for services (e.g., postpartum visit, family planning, contraception, etc.)',
              values: [formatPerc(report.healthyBirthSpacing_followedUpOnReferals), '', ''],
              displayDefault: true,
              note: report.healthyBirthSpacing_followedUpOnReferalsNote,
            },

            {
              description: 'Estimated percentage of unintended pregnancies/births averted due to HealthySteps',
              values: ['', formatPerc(report.healthyBirthSpacing_lowEstimate), formatPerc(report.healthyBirthSpacing_highEstimate)],
              displayDefault: true,
              note: report.healthyBirthSpacing_estimatesNote
            },
          ]
        },
        {
          title: 'Intimate Partner Violence',
          visible: interventionsChecklist.intimidatePartnerViolence,
          lines: [
            {
              description: 'Number of HealthySteps women enrolled in Medicaid who discussed an IPV problem with HealthySteps Specialist in past year',
              values: [report.intimatePartnerViolence_discussedProblem, '', ''],
              displayDefault: true,
              note: report.intimatePartnerViolence_discussedProblemNote,
            },
            {
              description: 'Number of HS mothers enrolled in Medicaid who received counseling or related services from HSS and/or were referred for services',
              values: [report.intimatePartnerViolence_receivedCounseling, '', ''],
              displayDefault: true,
              note: report.intimatePartnerViolence_receivedCounselingNote,
            },
            {
              description: 'Percentage of severe IPV cases with improvement due to HealthySteps',
              values: ['', formatPerc(report.intimatePartnerViolence_lowEstimate), formatPerc(report.intimatePartnerViolence_highEstimate)],
              displayDefault: true,
              note: report.intimatePartnerViolence_estimatesNote,
            },
          ],
        },
        {
          title: 'Breastfeeding',
          visible: interventionsChecklist.breastfeeding,
          lines: [
            {
              description: 'Number of caregivers (of Medicaid/CHIP-enrolled children) who discussed a need for breastfeeding advice/assistance with a HealthySteps Specialist',
              values: [report.breastfeeding_discussedProblem, '', ''],
              displayDefault: true,
              note: report.breastfeeding_discussedProblemNote,
            },
            {
              description: 'Number of caregivers (of Medicaid/CHIP-enrolled children) received counseling or related services from a HS Specialist and/or were referred for such services',
              values: [report.breastfeeding_receivedCounseling, '', ''],
              displayDefault: true,
              note: report.breastfeeding_receivedCounselingNote,
            },
            {
              description: 'Percentage of caregivers who continued breastfeeding due to HealthySteps',
              values: ['', formatPerc(report.breastfeeding_lowEstimate), formatPerc(report.breastfeeding_highEstimate)],
              displayDefault: true,
              note: report.breastfeeding_estimatesNote,
            },
            {
              description: 'Percentage of caregivers who continued exclusive breastfeeding due to HealthySteps',
              values: ['', formatPerc(report.breastfeeding_lowEstimateExclusive), formatPerc(report.breastfeeding_highEstimateExclusive)],
              displayDefault: true,
              note: report.breastfeeding_estimatesExclusiveNote,
            },
          ],
        },
        {
          title: 'Smoking Cessation',
          visible: interventionsChecklist.smokeCessation,
          lines: [
            {
              description: 'Number of Medicaid-enrolled caregivers who discussed a problem with smoking or any other tobacco product use with a HS Specialist and/or were referred for services',
              values: [report.smokeCessation_discussedProblem, '', ''],
              displayDefault: true,
              note: report.smokeCessation_discussedProblemNote,
            },
            {
              description: 'Percentage of Medicaid-enrolled caregivers who smoke or use tobacco products, and quit due to HealthySteps',
              values: ['', formatPerc(report.smokeCessation_lowEstimate), formatPerc(report.smokeCessation_highEstimate)],
              displayDefault: true,
              note: report.smokeCessation_estimatesNote,
            },
          ]
        },
        {
          title: 'Diapers',
          visible: interventionsChecklist.diapers,
          lines: [
            {
              description: 'Number of caregivers (of Medicaid/CHIP-enrolled children) provided diapers onsite or referred externally to a diaper bank/local non-profit',
              values: [report.diapers_numberCaregiversProvidedDiapersOrReferred, '', ''],
              displayDefault: true,
            },
            {
              description: 'Estimated percent of caregivers(of Medicaid/CHIP-enrolled children) who receive diapers at no cost due to HS',
              values: ['', formatPerc(report.diapers_lowEstimate), formatPerc(report.diapers_highEstimate)],
              displayDefault: true,
              note: report.diapers_estimatesNote,
            },
          ]
        },
        {
          title: 'Food Security',
          visible: interventionsChecklist.foodSecurity,
          lines: [
            {
              description: 'Number of Medicaid-enrolled caregivers identified as experiencing food insecurity and referred for SNAP, WIC, or other long-term ongoing food support',
              values: [report.foodSecurity_numberOfCaregiversFoodInsecureAndReferred, '', ''],
              displayDefault: true,
              note: report.foodSecurity_numberOfCaregiversFoodInsecureAndReferredNote,
            },
            {
              description: 'Percentage of Medicaid-enrolled caregivers referred for food assistance who receive ongoing food support due to HS involvement',
              values: ['', formatPerc(report.foodSecurity_lowEstimate), formatPerc(report.foodSecurity_highEstimate)],
              displayDefault: true,
              note: report.foodSecurity_estimatesNote,
            },
          ]
        },
        {
          title: 'Positive Parenting',
          visible: interventionsChecklist.positiveParenting,
          lines: [
            {
              description: 'Number of families (caregiver and Medicaid/CHIP-enrolled child) who are referred to a post-natal home visiting program',
              values: [report.positiveParenting_numberOfFamiliesReferredToPostNatalHomeVisitingPrograms, '', ''],
              displayDefault: true,
            },
            {
              description: 'Number of parents/guardians/caretakers who are provided sustained positive parenting services and supports by the HS Specialist and/or referred to a parenting program (e.g., Triple P)',
              values: [report.positiveParenting_numberOfGuardiansProvidedPositiveParentingOrReferredToParentingProgram, '', ''],
              displayDefault: true,
            },
            {
              description: 'Percent of families (caregiver and Medicaid/CHIP-enrolled child) referred to a home visiting program who are successfully connected and receive services',
              values: ['', formatPerc(report.positiveParenting_lowEstimate), formatPerc(report.positiveParenting_highEstimate)],
              displayDefault: true,
              note: report.positiveParenting_estimatesNote,
            },
          ]
        },
      ]
    }
  ];
};

export default {
  getCostSavingsReportBreakdown
};